import { format, formatDistanceToNow } from 'date-fns';
import type { CrmRequiredActionItem } from '../crm/requiredActions';
import type { PastDuePayrollPeriod } from '../payrollPastDuePeriods';

export function buildPayrollPeriodRequiredActions(
  periods: PastDuePayrollPeriod[],
): CrmRequiredActionItem[] {
  return periods.map((period) => {
    const isOverdue = period.daysPastDue > 0;
    const statusLabel = isOverdue
      ? period.daysPastDue === 1
        ? '1 day past due'
        : `${period.daysPastDue} days past due`
      : period.daysPastDue === 0
        ? 'Due today'
        : period.daysPastDue === -1
          ? 'Due in 1 day'
          : `Due in ${Math.abs(period.daysPastDue)} days`;

    return {
      kind: 'payroll_period' as const,
      payrollPeriodStart: format(period.start, 'yyyy-MM-dd'),
      payrollPeriodEnd: format(period.end, 'yyyy-MM-dd'),
      payrollPeriodDaysPastDue: period.daysPastDue,
      title: `${format(period.start, 'MMM d')} – ${format(period.end, 'MMM d, yyyy')}`,
      customerName: isOverdue ? 'Payroll · Overdue' : 'Payroll · Due',
      subtitle: `${statusLabel} · $${period.totalEarnings.toFixed(2)} unpaid · ${period.resourceCount} ${
        period.resourceCount === 1 ? 'resource' : 'resources'
      }`,
      sortOrder: -period.daysPastDue,
      createdAt: format(period.end, 'yyyy-MM-dd'),
    };
  });
}

export type PayrollRequestType = 'change' | 'add' | 'add_flat_rate';

export interface PayrollRequiredActionRow {
  id: string;
  request_type: string;
  status: string;
  requested_at: string;
  created_at: string;
  resource_name?: string;
}

const PENDING_PAYROLL_REQUEST_TYPES = new Set<PayrollRequestType>([
  'change',
  'add',
  'add_flat_rate',
]);

function getPayrollRequestLabel(requestType: string): string {
  switch (requestType) {
    case 'change':
      return 'time change';
    case 'add':
      return 'add entry';
    case 'add_flat_rate':
      return 'add flat rate';
    default:
      return 'request';
  }
}

export function buildPayrollRequiredActions(
  requests: PayrollRequiredActionRow[],
  now: Date = new Date(),
): CrmRequiredActionItem[] {
  const items: CrmRequiredActionItem[] = [];

  for (const request of requests) {
    if (request.status !== 'pending') continue;
    if (!PENDING_PAYROLL_REQUEST_TYPES.has(request.request_type as PayrollRequestType)) continue;

    const resourceName = request.resource_name?.trim() || 'Team member';
    const requestLabel = getPayrollRequestLabel(request.request_type);
    const requestedAt = request.requested_at || request.created_at;
    const requested = new Date(requestedAt);
    const requestedMs = requested.getTime();

    items.push({
      kind: 'payroll_request',
      payrollRequestId: request.id,
      title: resourceName,
      customerName: `Payroll · ${requestLabel}`,
      subtitle: Number.isNaN(requestedMs)
        ? 'Pending review'
        : `Requested ${formatDistanceToNow(requested, { addSuffix: true })}`,
      sortOrder: Number.isNaN(requestedMs) ? now.getTime() : requestedMs,
      createdAt: request.created_at,
    });
  }

  items.sort((a, b) => a.sortOrder - b.sortOrder);
  return items;
}
